import React, { useEffect, useMemo } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { EnvelopeSimple, WhatsappLogo } from '@phosphor-icons/react'
import { Slot } from '@radix-ui/react-slot'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button, ButtonLoading } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp'
import { Spinner } from '@/components/ui/spinner'
import { AuthCard } from '@/components/view/auth-view'

import { authResendOtp, authVerifyOtp } from '@/services/auth/auth.gql'
import { profileAccount } from '@/services/profile/profile.gql'

import { cn } from '@/lib/utils'

import { useCountdown } from '@/hooks/use-countdown'

export const Route = createFileRoute('/auth/verification')({
  component: RouteComponent,
})

type Data = { option: (typeof VERIFICATION_OPTIONS)[number]; data: string }
type Step = {
  type: 'choose' | 'input' | 'success'
  data: Data
}

function RouteComponent() {
  const [step, setStep] = React.useState<Step>({
    type: 'choose',
    data: undefined!,
  })

  if (step.type === 'success') {
    return <PageSuccess />
  }

  if (step.type === 'input') {
    return (
      <PageInputCode
        data={step.data}
        onBack={() => setStep({ type: 'choose', data: undefined! })}
        onSuccess={(data) => setStep({ type: 'success', data })}
      />
    )
  }

  return (
    <PageChooseOptions onSuccess={(data) => setStep({ type: 'input', data })} />
  )
}

/* ----------------------------- CHOOSE OPTIONS ----------------------------- */
function PageChooseOptions({ onSuccess }: { onSuccess: (data: Data) => void }) {
  const { data } = useSuspenseQuery(profileAccount.qry())
  const form = useForm<FormOptionsDto>({
    resolver: zodResolver(FormOptionsDto),
    defaultValues: { option: 'email' },
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ option }: FormOptionsDto) => {
      await authResendOtp.api({ req: { via: option } })
      return {
        option,
        data:
          option === 'email'
            ? data?.getProfile?.profile?.email
            : data?.getProfile?.profile?.phone,
      }
    },
    onSuccess: (data) => {
      onSuccess(data as Data)
    },
  })

  const { control } = form

  const hasPhone = data?.getProfile?.profile?.phone
  const selected = useWatch({ control, name: 'option' })

  const handleSubmit = form.handleSubmit((data) => {
    toast.promise(mutateAsync(data), {
      loading: 'Mengirimkan kode OTP',
      success: 'Kode OTP berhasil dikirim',
      error: 'Gagal mengirim kode OTP',
    })
  }, console.error)

  return (
    <AuthCard
      title="Verifikasi Akun"
      desc="Pilih salah satu akun terkait (nomor HP/email) yang akan digunakan untuk pengiriman OTP untuk verifikasi akun."
    >
      <Form {...form}>
        <form onSubmit={handleSubmit}>
          <ul className="space-y-2">
            <OptionItem
              label="Kirim OTP via email terdaftar"
              desc={data?.getProfile?.profile?.email || ''}
              icon={
                <EnvelopeSimple
                  weight="fill"
                  className="fill-persian-red-600"
                />
              }
              selected={selected === 'email'}
              onSelect={() => form.setValue('option', 'email')}
            />
            {hasPhone && (
              <OptionItem
                label="Kirim OTP via WhatsApp terdaftar"
                desc={data?.getProfile?.profile?.phone || ''}
                icon={
                  <WhatsappLogo
                    size={32}
                    weight="fill"
                    className="fill-persian-red-600"
                  />
                }
                onSelect={() => form.setValue('option', 'whatsapp')}
                selected={selected === 'whatsapp'}
              />
            )}
          </ul>
          <div className="mt-4 flex justify-end">
            <ButtonLoading variant={'yes'} size={'lg'} isLoading={isPending}>
              Kirimkan kode OTP
            </ButtonLoading>
          </div>
        </form>
      </Form>
    </AuthCard>
  )
}

const VERIFICATION_OPTIONS = ['whatsapp', 'email'] as const

type FormOptionsDto = z.infer<typeof FormOptionsDto>
const FormOptionsDto = z.object({
  option: z.enum(VERIFICATION_OPTIONS),
})

type OptionItemProps = {
  selected: boolean
  onSelect: () => void
  icon: React.ReactNode
  label: string
  desc: string
}

function OptionItem({
  selected,
  onSelect,
  icon,
  label,
  desc,
}: OptionItemProps) {
  return (
    <li>
      <button
        className={cn(
          'flex w-full cursor-pointer gap-4 rounded-lg border border-[#E2E2E2] p-3 font-medium focus:outline-2',
          selected && 'border-persian-red-600'
        )}
        onClick={onSelect}
        type="button"
      >
        <div className="flex aspect-[1/1] w-16 shrink-0 items-center justify-center rounded-md bg-[#EEE8FF]">
          <Slot className="h-8 w-8">{icon}</Slot>
        </div>
        <div className="flex grow flex-col gap-1 text-left">
          <p className="max-w-full truncate">{label}</p>
          <p className="text-xl font-bold">{desc}</p>
        </div>
      </button>
    </li>
  )
}

/* ------------------------------- INPUT CODE ------------------------------- */

const OTP_LENGTH = 6

function PageInputCode({
  data,
  onBack,
  onSuccess,
}: {
  data: Data
  onBack: () => void
  onSuccess: (data: Data) => void
}) {
  const form = useForm<CodeFormDto>({
    mode: 'all',
    resolver: zodResolver(CodeFormDto),
    defaultValues: { otp: '' },
  })
  const { setValue, watch } = form

  const [seconds, { startCountdown, resetCountdown }] = useCountdown({
    countStart: 60, // 1 Minutes
    countStop: 0,
    intervalMs: 1000,
  })

  useEffect(() => {
    startCountdown()
  }, [startCountdown])

  const timer = useMemo(() => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }, [seconds])

  const resendMutation = useMutation({
    mutationFn: async () => {
      await authResendOtp.api({ req: { via: data.option } })
    },
    onSettled: () => {
      resetCountdown()
      startCountdown()
    },
  })

  const verifyOtpMutation = useMutation({
    mutationFn: async ({ otp }: CodeFormDto) => {
      await authVerifyOtp.api({ req: { otp } })
      return { data }
    },
    onSuccess: ({ data }) => {
      onSuccess(data as Data)
    },
  })

  const onResend = () => {
    toast.promise(resendMutation.mutateAsync(), {
      loading: 'Mengirimkan kode OTP',
      success: 'Kode OTP berhasil dikirim',
      error: 'Gagal mengirim kode OTP',
    })
  }

  const handleSubmit = form.handleSubmit((data) => {
    toast.promise(verifyOtpMutation.mutateAsync(data), {
      loading: 'Verifikasi kode OTP',
      success: 'Kode OTP berhasil divalidasi',
      error: 'Kode OTP gagal diverifikasi',
    })
  }, console.error)

  const isRetryEnabled = seconds <= 0 && resendMutation.isIdle

  return (
    <AuthCard
      title="Verifikasi OTP"
      desc="Harap masukkan kode OTP yang sudah dikirimkan ke email/no. HP terkait"
    >
      <form onSubmit={handleSubmit}>
        <div className="w-full space-y-3">
          <InputOTP
            value={watch('otp')}
            onChange={(otp) => setValue('otp', otp)}
            maxLength={6}
            pattern={REGEXP_ONLY_DIGITS}
            autoFocus
          >
            <InputOTPGroup className="w-full">
              {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                <InputOTPSlot
                  key={index}
                  index={index}
                  className="border-persian-red-600 m-2 h-16 basis-1/6 !rounded-md border text-xl font-bold focus:outline-0"
                />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <p className="text-sm">
            Kirim ulang OTP dalam <span className="font-bold">{timer}</span>
          </p>
          <p className="flex text-sm">
            Tidak menerima kode OTP?{' '}
            <button
              disabled={!isRetryEnabled}
              className={
                'text-persian-red-600 ml-1 flex flex-row font-semibold underline disabled:opacity-60 disabled:hover:cursor-not-allowed'
              }
              onClick={onResend}
              type="button"
            >
              Kirim Ulang
              {resendMutation.isPending && (
                <Spinner className="fill-persian-red-600 mt-0.5 ml-1" />
              )}
            </button>
          </p>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2 [&>*]:flex-1">
          <Button
            variant={'outline'}
            size={'lg'}
            type="button"
            onClick={onBack}
            disabled={verifyOtpMutation.isPending}
          >
            Kembali
          </Button>
          <ButtonLoading
            variant={'yes'}
            size={'lg'}
            isLoading={verifyOtpMutation.isPending}
            disabled={watch('otp').length !== OTP_LENGTH}
          >
            Verifikasi Sekarang
          </ButtonLoading>
        </div>
      </form>
    </AuthCard>
  )
}

type CodeFormDto = z.infer<typeof CodeFormDto>
const CodeFormDto = z.object({ otp: z.string().min(OTP_LENGTH) })

/* ------------------------------ SUCCESS PAGE ------------------------------ */
function PageSuccess() {
  return (
    <AuthCard>
      <div className="flex w-full flex-col items-center justify-center gap-8">
        <div className="relative" style={{ maxWidth: '320px' }}>
          <img
            className="w-full"
            src="/img/auth/verified.png"
            alt="Verified"
            width={300}
            height={252}
          />
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-4 text-center">
          <p className="font-tomorrow text-2xl font-bold">
            Selamat bergabung di Yes Academy!
          </p>
          <p>Akun telah terverifikasi, silakan login</p>
          <Button className="mt-4 w-full" variant={'yes'} asChild size={'lg'}>
            <Link from={Route.fullPath} to="/" replace>
              Masuk
            </Link>
          </Button>
        </div>
      </div>
    </AuthCard>
  )
}
